import { sanitizeHtml } from "./sanitizeHtml";

/**
 * Polls travel as a marker inside the message HTML, like Drive files do
 * (fileMarkers.ts), so the one message pipeline — channels, 1:1 and group
 * chats, meeting chats, plus Mail's chat dock, search and the chat-list
 * preview — carries them without a schema change:
 *
 *   <div data-athavita-poll="<pollId>">📊 Where should we have lunch?</div>
 *
 * DOMPurify keeps data-* attributes, and the "📊 question" text is what older
 * clients and Mail show. Only the id is trusted, and only after it validates
 * as a GUID; the card then loads the poll from the BFF, which answers only to
 * people in the poll's own conversation.
 */
export const POLL_ATTR = "data-athavita-poll";

const GUID = /^[0-9a-fA-F]{8}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{12}$/;

export function isPollId(id: string | null | undefined): id is string {
    return typeof id === "string" && GUID.test(id);
}

function escapeAttr(value: string): string {
    return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escapeText(value: string): string {
    return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function pollMarkerHtml(pollId: string, question: string): string {
    return `<div ${POLL_ATTR}="${escapeAttr(pollId)}">📊 ${escapeText(question)}</div>`;
}

/** True when a body carries a poll (such messages aren't edited — the poll is the content). */
export function hasPollMarker(html: string | null | undefined): boolean {
    return Boolean(html && html.includes(POLL_ATTR));
}

/**
 * Pull the poll markers out of ALREADY-SANITISED html. Returns the rest
 * (sanitised again after the markers are removed) and the validated poll ids,
 * in message order.
 */
export function splitPollMarkers(cleanHtml: string): { html: string; pollIds: string[] } {
    if (!cleanHtml.includes(POLL_ATTR)) return { html: cleanHtml, pollIds: [] };
    // DOMParser documents are inert — nothing runs and nothing loads.
    const doc = new DOMParser().parseFromString(cleanHtml, "text/html");
    const pollIds: string[] = [];
    const seen = new Set<string>();
    for (const el of Array.from(doc.body.querySelectorAll(`[${POLL_ATTR}]`))) {
        const id = el.getAttribute(POLL_ATTR) ?? "";
        el.remove();
        if (!isPollId(id) || seen.has(id.toLowerCase())) continue;
        seen.add(id.toLowerCase());
        pollIds.push(id);
    }
    return { html: sanitizeHtml(doc.body.innerHTML), pollIds };
}
